import type React from "react"
import { createContext, useContext, useEffect, useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { getWordPressUser, logoutWordPress, type WordPressUser } from "@/lib/wordpress-auth"

interface WordPressAuthContextType {
  user: WordPressUser | null
  isLoading: boolean
  logout: () => void
  login: (user: WordPressUser) => void
  refreshUser: () => Promise<void>
}

const WordPressAuthContext = createContext<WordPressAuthContextType>({
  user: null,
  isLoading: true,
  logout: () => {},
  login: () => {},
  refreshUser: async () => {},
})

export function WordPressAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<WordPressUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()
  const { pathname } = useLocation()

  useEffect(() => {
    loadUser()
  }, [])

  useEffect(() => {
    if (!isLoading && !user && pathname !== "/login") {
      navigate("/login")
    }
  }, [isLoading, user, pathname, navigate])

  async function loadUser() {
    setIsLoading(true)
    const wpUser = await getWordPressUser()
    setUser(wpUser)
    setIsLoading(false)
  }

  const refreshUser = async () => {
    // Try to get fresh user data including profile picture from REST API
    try {
      const config = (window as any).WP_COCKPIT
      if (config?.restApiUrl) {
        const response = await fetch(`${config.restApiUrl}kj-cockpit/v1/user-profile`, {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            'X-WP-Nonce': config.restNonce
          }
        })
        
        if (response.ok) {
          const profileData = await response.json()
          if (profileData.success) {
            // Update user with fresh profile data
            const currentUser = await getWordPressUser()
            if (currentUser) {
              const updatedUser = {
                ...currentUser,
                firstName: profileData.data.firstName || currentUser.firstName,
                lastName: profileData.data.lastName || currentUser.lastName,
                profilePicture: profileData.data.profilePicture || currentUser.profilePicture
              }
              setUser(updatedUser)
              
              // Update localStorage cache too
              try {
                localStorage.setItem('wp_user', JSON.stringify(updatedUser))
              } catch (e) {}
              
              return
            }
          }
        }
      }
    } catch (error) {
      console.error('[refreshUser] Failed to get fresh profile data:', error)
    }
    
    // Fallback to regular refresh
    const wpUser = await getWordPressUser()
    setUser(wpUser)
  }

  const logout = async () => {
    await logoutWordPress()
    setUser(null)
  }

  const login = (newUser: WordPressUser) => {
    setUser(newUser)
    
    // Get computed CSS variable values from the root element
    const root = document.documentElement
    const computedStyle = getComputedStyle(root)
    const background = computedStyle.getPropertyValue('--background').trim()
    const foreground = computedStyle.getPropertyValue('--foreground').trim()
    
    // Create a temporary element to get the actual computed border color
    const temp = document.createElement('div')
    temp.className = 'border-primary'
    temp.style.position = 'absolute'
    temp.style.visibility = 'hidden'
    document.body.appendChild(temp)
    const primaryBorderColor = getComputedStyle(temp).borderColor
    document.body.removeChild(temp)
    
    // Create a persistent overlay that matches the React overlay exactly
    const overlay = document.createElement('div')
    overlay.style.cssText = `
      position: fixed;
      inset: 0;
      background-color: hsl(${background} / 0.8);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      z-index: 50;
      display: flex;
      align-items: center;
      justify-content: center;
    `
    
    const content = document.createElement('div')
    content.style.cssText = `
      text-align: center;
      display: flex;
      flex-direction: column;
      gap: 16px;
    `
    
    const spinner = document.createElement('div')
    spinner.style.cssText = `
      display: inline-block;
      width: 48px;
      height: 48px;
      border: 4px solid ${primaryBorderColor};
      border-right-color: transparent;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto;
    `
    
    const text = document.createElement('p')
    text.style.cssText = `
      font-size: 18px;
      line-height: 28px;
      font-weight: 500;
      color: hsl(${foreground});
      margin: 0;
    `
    text.textContent = 'Signing you in...'
    
    const style = document.createElement('style')
    style.textContent = `
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    `
    document.head.appendChild(style)
    
    content.appendChild(spinner)
    content.appendChild(text)
    overlay.appendChild(content)
    document.body.appendChild(overlay)
    
    // Navigate to dashboard - the overlay will stay visible during reload
    const dashboardUrl = window.location.origin + (window as any).WP_COCKPIT?.mountPath + '/dashboard'
    window.location.replace(dashboardUrl)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <WordPressAuthContext.Provider value={{ user, isLoading, logout, login, refreshUser }}>{children}</WordPressAuthContext.Provider>
  )
}

export function useWordPressAuth() {
  return useContext(WordPressAuthContext)
}
